import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useMatch, useNavigate } from 'react-router';
import { DEFAULT_LAYOUT, OAUTH_PROVIDER_LABELS, type OAuthProviderId } from '@clanfire/shared';
import { ApiError, errorMessage } from '@/api/client';
import { oauthErrorMessage } from '@/components/auth/OAuthButtons';
import { GettingStartedCard } from '@/components/gettingStarted/GettingStartedCard';
import { ChannelSidebar } from '@/components/layout/ChannelSidebar';
import { ConnectionBanner } from '@/components/layout/ConnectionBanner';
import { EmailReminder } from '@/components/layout/EmailReminder';
import { DmSidebar } from '@/components/layout/DmSidebar';
import { ServerRail } from '@/components/layout/ServerRail';
import { UserPanel, VoicePanel } from '@/components/layout/UserPanel';
import { ModalHost } from '@/components/modals/ModalHost';
import { Button } from '@/components/ui/Button';
import { ErrorState } from '@/components/ui/Feedback';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { useDocumentTitle, useIdleReporter, useIsDesktop, useIsWide, useTypingPruner } from '@/hooks/useAppEffects';
import { useGlobalShortcuts } from '@/hooks/useGlobalShortcuts';
import { SAFE_MODE } from '@/lib/appearance';
import { registerNavigator } from '@/lib/navigation';
import { endSessionLocally, loadBootstrap } from '@/lib/session';
import { saveSettings } from '@/lib/settings';
import { cn } from '@/lib/utils';
import { connectSocket, disconnectSocket, setSocketUnauthorizedHandler } from '@/realtime/socket';
import { useAuth } from '@/stores/auth';
import { useGettingStarted } from '@/stores/gettingStarted';
import { toast, useUi } from '@/stores/ui';

export function AppLayout() {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const navOpen = useUi((s) => s.navOpen);
  const setNavOpen = useUi((s) => s.setNavOpen);
  const serverMatch = useMatch('/app/:serverId/*');
  const serverId = serverMatch?.params.serverId && serverMatch.params.serverId !== '@me' ? serverMatch.params.serverId : undefined;

  useEffect(() => {
    registerNavigator((path) => navigate(path));
    return () => registerNavigator(null);
  }, [navigate]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      await loadBootstrap();
      connectSocket();
      setState('ready');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED') {
        // The rules changed since we loaded them: show the "confirm your email" screen.
        useAuth.setState((s) => ({ config: { ...s.config, emailVerification: 'required' }, user: s.user && { ...s.user, emailVerified: false } }));
        return;
      }
      setError(errorMessage(err, 'Could not load your data.'));
      setState('error');
    }
  }, []);

  useEffect(() => {
    setSocketUnauthorizedHandler(() => {
      endSessionLocally();
      toast.info('Your session has expired. Please log in again.');
    });
    void load();
    return () => {
      setSocketUnauthorizedHandler(null);
      disconnectSocket();
    };
  }, [load]);

  // Back from connecting Google or GitHub in Settings → Security (#oauth=linked:google or #oauth=error:<code>:google).
  useEffect(() => {
    if (state !== 'ready') return;
    const match = /^#oauth=(linked|error):([a-z_]+)(?::([a-z]+))?$/.exec(location.hash);
    if (!match) return;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true });
    const [, kind, first, second] = match;
    if (kind === 'linked') {
      const name = OAUTH_PROVIDER_LABELS[first as OAuthProviderId] ?? 'Your account';
      toast.success(`${name} connected. You can use it to sign in now.`);
    } else {
      toast.error(oauthErrorMessage(first!, second ?? null)!);
    }
    useUi.getState().openModal({ type: 'userSettings', section: 'security' });
  }, [state, location, navigate]);

  // New accounts get a short welcome tour, once.
  useEffect(() => {
    if (state === 'ready' && useGettingStarted.getState().showTour && !useUi.getState().modal) useUi.getState().openModal({ type: 'welcomeTour' });
  }, [state]);

  // Close the mobile drawer whenever the route changes, or on Escape.
  useEffect(() => setNavOpen(false), [location.pathname, setNavOpen]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && !document.querySelector('dialog[open]')) setNavOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen, setNavOpen]);

  // The member list is a column on wide screens but an overlay on narrow ones:
  // when the window crosses the breakpoint, show/hide it to match (and your layout).
  const isWide = useIsWide();
  const wasWide = useRef(isWide);
  const layout = useAuth((s) => s.settings?.layout ?? DEFAULT_LAYOUT);
  const membersByDefault = layout.memberList === 'SHOWN';
  useEffect(() => {
    if (wasWide.current === isWide || document.fullscreenElement) return;
    wasWide.current = isWide;
    useUi.getState().setMembersOpen(isWide && membersByDefault);
  }, [isWide, membersByDefault]);
  // Your layout decides whether it starts open (and changing that setting applies it).
  useEffect(() => {
    if (state === 'ready') useUi.getState().setMembersOpen(window.innerWidth >= 1200 && membersByDefault);
  }, [state, membersByDefault]);

  useIdleReporter();
  useDocumentTitle();
  useTypingPruner();
  useGlobalShortcuts();

  if (state === 'loading') return <FullPageSpinner label="Loading your servers…" />;
  if (state === 'error') {
    return (
      <div className="flex h-dvh items-center justify-center bg-surface">
        <ErrorState message={error} onRetry={load} />
      </div>
    );
  }

  // Your layout moves things around on bigger screens; phones keep the slide-in menu.
  const railOnTop = isDesktop && layout.serverList === 'TOP';
  const channelsOnRight = isDesktop && layout.channelList === 'RIGHT';
  const sidebar = (
    <aside data-cf="channel-list" className="flex w-[264px] shrink-0 flex-col bg-sidebar" aria-label={serverId ? 'Channels' : 'Direct messages'}>
      {serverId ? <ChannelSidebar serverId={serverId} /> : <DmSidebar />}
      <GettingStartedCard />
      <VoicePanel />
      <UserPanel />
    </aside>
  );
  const main = (
    <main id="main-content" data-cf="main" tabIndex={-1} className="relative flex min-w-0 flex-1 flex-col focus:outline-none">
      <SafeModeBanner />
      <ConnectionBanner />
      <EmailReminder />
      <Outlet />
    </main>
  );

  return (
    <div
      data-app-shell
      data-cf="app"
      data-cf-server-list={railOnTop ? 'top' : 'left'}
      data-cf-channel-list={channelsOnRight ? 'right' : 'left'}
      className={cn('flex h-dvh overflow-hidden bg-surface', railOnTop && 'flex-col')}
    >
      {/* First thing a keyboard user reaches: jump past the server and channel lists. */}
      <a
        href="#main-content"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById('main-content');
          (main?.querySelector<HTMLElement>('textarea') ?? main?.querySelector<HTMLElement>('[role="log"]') ?? main)?.focus();
        }}
        className="sr-only z-50 rounded-full bg-accent font-semibold text-accent-fg shadow-pop focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:px-4! focus:py-2!"
      >
        Skip to chat
      </a>
      {isDesktop ? (
        <>
          {railOnTop && <ServerRail activeServerId={serverId} horizontal />}
          <div className="flex min-h-0 flex-1">
            {!railOnTop && <ServerRail activeServerId={serverId} />}
            {!channelsOnRight && sidebar}
            {main}
            {channelsOnRight && sidebar}
          </div>
        </>
      ) : (
        <>
          {navOpen && <div className="fixed inset-0 z-30 animate-fade-in bg-black/55" onClick={() => setNavOpen(false)} aria-hidden />}
          <div
            className={cn('fixed inset-y-0 left-0 z-40 flex h-full shrink-0 transition-transform duration-200 ease-out', navOpen ? 'shadow-pop' : '-translate-x-full')}
            aria-hidden={!navOpen ? true : undefined}
            inert={!navOpen ? true : undefined}
          >
            <ServerRail activeServerId={serverId} />
            {sidebar}
          </div>
          {main}
        </>
      )}
      <ModalHost />
    </div>
  );
}

/** While ?safe is in the address: says your custom CSS is off, with ways out. */
function SafeModeBanner() {
  const enabled = useAuth((s) => !!s.settings?.customCssEnabled);
  if (!SAFE_MODE || !enabled) return null;
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line bg-sunken px-4 py-2 text-sm">
      <span className="flex-1">
        <strong>Safe mode:</strong> your custom CSS is off until you reload without <code className="font-mono">?safe</code>.
      </span>
      <Button size="sm" variant="secondary" onClick={() => void saveSettings({ customCssEnabled: false })}>
        Turn custom CSS off
      </Button>
      <Button size="sm" variant="ghost" onClick={() => location.assign(location.pathname)}>
        Reload with it
      </Button>
    </div>
  );
}
